import { logError } from "@entrilia/shared/logging";
import type {
  ResourceChangeProgressNotification,
  ResourceChangedNotification,
} from "../api/types/resourceChangesTypes";

export interface ReloadQueue {
  request: () => void;
  setPaused: (paused: boolean) => void;
  dispose: () => void;
}

const MIN_RELOAD_INTERVAL_MS = 2_000;

/** Runs one reload at a time, at least `MIN_RELOAD_INTERVAL_MS` apart, and keeps at most one pending, so a burst of events becomes a few reloads. */
export const createReloadQueue = (reload: () => Promise<void>): ReloadQueue => {
  let isReloading = false;
  let isPending = false;
  let isPaused = false;
  let isDisposed = false;
  let lastStartedAt = Number.NEGATIVE_INFINITY;
  let delayTimer: ReturnType<typeof setTimeout> | undefined;

  const runPending = () => {
    if (!isPending || isReloading || isPaused || isDisposed || delayTimer !== undefined) {
      return;
    }

    const delayMs = lastStartedAt + MIN_RELOAD_INTERVAL_MS - Date.now();
    if (delayMs > 0) {
      delayTimer = setTimeout(() => {
        delayTimer = undefined;
        runPending();
      }, delayMs);
      return;
    }

    run();
  };

  const run = async () => {
    isReloading = true;
    isPending = false;
    lastStartedAt = Date.now();

    try {
      await reload();
    } catch (error) {
      logError(error, "[reloadQueue] reload");
    }

    isReloading = false;
    runPending();
  };

  return {
    request: () => {
      isPending = true;
      runPending();
    },
    setPaused: (paused) => {
      isPaused = paused;
      runPending();
    },
    dispose: () => {
      isDisposed = true;
      isPending = false;
      clearTimeout(delayTimer);
      delayTimer = undefined;
    },
  };
};

export type ResourceChangeAction =
  { type: "reload" } | { type: "showProgress"; progress: ResourceChangeProgressNotification } | { type: "ignore" };

interface ResourceChangeState {
  handlesProgress: boolean;
  isOperationRunning: boolean;
}

export const getResourceChangeAction = (
  notification: ResourceChangedNotification,
  { handlesProgress, isOperationRunning }: ResourceChangeState
): ResourceChangeAction => {
  if (notification.kind !== "Progress") {
    return { type: "reload" };
  }

  if (!handlesProgress) {
    return { type: "ignore" };
  }

  // A new operation starts with Progress, not Changed, and a late Progress can follow Completed: a reload tells them apart.
  if (!isOperationRunning) {
    return { type: "reload" };
  }

  return notification.progress ? { type: "showProgress", progress: notification.progress } : { type: "ignore" };
};
